import React, { lazy, Suspense } from 'react';
import DeloitteLoader from '../../components/ui/DeloitteLoader';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { NavMenuOptions } from '../../constants/AppConstants';
import { Toast } from '../../components/notifications/Toast/Toast';
import useDeviceDetect from '../../hooks/UseDeviceDetect';
import AuthMonitor from '../../libs/helper/AuthMonitor';
import { isAuthenticated } from '../../libs/helper/HelperFunctions';
import { IApplicationState, useAppSelector } from '../../libs/store/reducers';
import { routes } from '../../navigation/Routes';
import PrivateRoute from '../../components/Auth/privateRoute/PrivateRoute';

// All page-level components are code-split via React.lazy so the initial
// JS bundle only contains the shell (router, auth, layout chrome).
// Each route's chunk is fetched on demand the first time it's visited.
const AuthenticationPage = lazy(
  () => import('../../components/Auth/AssetAuthenticationPage/AuthenticationPage'),
);
const CreateCloudEnvironments = lazy(
  () => import('../../pages/CreateCloudEnvironments/CreateCloudEnvironments'),
);
const IFrameDrawer = lazy(() => import('../../components/modals/IFrameDrawer/IFrameDrawer'));
const KeyContactsMobile = lazy(
  () => import('../../containers-mobile/KeyContactsMobile/KeyContactsMobile'),
);
const DashboardMobile = lazy(
  () => import('../../containers-mobile/LoginMobile/DashboardMobile/DashboardMobile'),
);
const LoginMobile = lazy(() => import('../../containers-mobile/LoginMobile/LoginMobile'));
const AccountManagement = lazy(
  () => import('../../pages/AccountManagement/AccountManagement'),
);
const Assessments = lazy(() => import('../../pages/Assessments/Assessments'));
const AssetAuthenticationRoutes = lazy(
  () => import('../AssetAuthenticationRoutes/AssetAuthenticationRoutes'),
);
const AssetAuthorisation = lazy(
  () => import('../../pages/AssetAuthorisation/AssetAuthorisation'),
);
const BlockedScreen = lazy(() => import('../BlockedScreen/BlockedScreen'));
const CBOLogin = lazy(() => import('../../pages/CBOLogin/CBOLogin'));
const ConfigureCBOJourney = lazy(
  () => import('../../pages/CBOConfigureJourney/ConfigureCBOJourney'),
);
const CompliancePolicies = lazy(
  () => import('../../pages/CompliancePolicies/CompliancePolicies'),
);
const IndustryCompliancePolicies = lazy(
  () => import('../../pages/CompliancePolicies/IndustryCompliancePolicies'),
);
const Dashboard = lazy(() => import('../Dashboard/Dashboard'));
const HomePage = lazy(() => import('../HomePage/HomePage'));
const DeliveryPipelines = lazy(
  () => import('../../pages/DeliveryPipeline/DeliveryPipelines'),
);
const FeedbackForm = lazy(() => import('../../pages/FeedbackForm/FeedbackForm'));
const GiveFeedbackForm = lazy(() => import('../../pages/FeedbackForm/GiveFeedbackForm'));
const FeedbackResponse = lazy(() => import('../../pages/FeedbackResponse/FeedbackResponse'));
const JobRuleSet = lazy(() => import('../../pages/JobRuleSet/JobRuleSet'));
const KeyContacts = lazy(() => import('../../pages/KeyContacts/KeyContacts'));
const Login = lazy(() => import('../Login/Login'));
const PipelineHistory = lazy(() => import('../../pages/PipelineHistory/PipelineHistory'));
const QuickLinks = lazy(() => import('../../pages/QuickLinks/QuickLinks'));
const UserManagement = lazy(() => import('../../pages/UserManagement/UserManagement'));
const WebAnalytics = lazy(() => import('../../pages/WebAnalytics/WebAnalytics'));
const Documentation = lazy(() => import('../../pages/Documentation/Documentation'));
const MigrateDashboard = lazy(() => import('../../pages/Migrate/MigrateDashboard'));
const Operate = lazy(() => import('../../pages/SAPOperate/Operate'));
const DigitalPMODashboard = lazy(() => import('../../pages/DigitalPMO/DigitalPMODashboard'));
const Test = lazy(() => import('../../pages/Migrate/TestTab/Test'));
const AssessDashboard = lazy(() => import('../../pages/Assess/AssessDashboard'));
const TransformDashboard = lazy(() => import('../../pages/Transform/TransformDashboard'));
const Capabilities = lazy(() => import('../Capabilities/Capabilities'));
const GenAiArchitectureDiagramCreation = lazy(
  () => import('../ArchitectureGeneration.tsx/ArchitectureGeneration'),
);
const CloudAssessment = lazy(() => import('../CloudAssessment/CloudAssessment'));

const RouteFallback = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      width: '100%',
    }}
  >
    <DeloitteLoader size={80} />
  </div>
);

const Layout = () => {
  const { isMobile } = useDeviceDetect();

  const { showToast, description, message, type } = useAppSelector(
    ({ toast: { showToast, description, message, type } }: IApplicationState) => ({
      showToast,
      description,
      message,
      type,
    }),
  );

  return (
    <>
      {isAuthenticated() && <AuthMonitor />}
      {showToast && Toast(description, message, type)}
      <Router>
        <Suspense fallback={<RouteFallback />}>
          <Switch>
          {/* ============================================ */}
          {/* INTEGRAL ROUTES - Core application features */}
          {/* ============================================ */}
          <Route exact path={routes.login.viewGeneric}>
            {isMobile ? <LoginMobile /> : <Login />}
          </Route>
          <Route exact path={routes.dashboard}>
            <PrivateRoute isMobile={isMobile}>
              {isMobile ? <DashboardMobile /> : <Dashboard />}
            </PrivateRoute>
          </Route>
          <Route exact path={routes.home}>
            <PrivateRoute isMobile={isMobile}>
              <HomePage />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.capabilities}>
            <PrivateRoute isMobile={isMobile}>
              <Capabilities />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.aiArchitectureGeneration}>
            <PrivateRoute hideHomePage={true}>
              <GenAiArchitectureDiagramCreation />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.aiCloudAssessments}>
            <PrivateRoute hideHomePage={true}>
              <CloudAssessment />
            </PrivateRoute>
          </Route>
          
          <Route exact path={routes.aiTerraformModules}>
            <PrivateRoute hideHomePage={true}>
              <CreateCloudEnvironments />
            </PrivateRoute>
          </Route>

          {/* ============================================ */}
          {/* AUTHENTICATION ROUTES - User access control */}
          {/* ============================================ */}
          <Route exact path={routes.authentication}>
            <AuthenticationPage />
          </Route>
          <Route exact path={routes.login.error}>
            <BlockedScreen />
          </Route>
          <Route path={routes.authentication}>
            <PrivateRoute hideHomePage>
              <AssetAuthenticationRoutes />
            </PrivateRoute>
          </Route>

          {/* ============================================ */}
          {/* KEEP FOR REFERENCE - Useful existing features */}
          {/* ============================================ */}
          
          {/* Management & Administration */}
          <Route exact path={routes.accountManagement}>
            <PrivateRoute>
              <AccountManagement />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.userManagement}>
            <PrivateRoute>
              <UserManagement />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.assetAuthorisation}>
            <PrivateRoute>
              <AssetAuthorisation />
            </PrivateRoute>
          </Route>

          {/* Operations & Pipelines */}
          <Route exact path={routes.manage}>
            <PrivateRoute isMobile={isMobile}>
              <Operate />
            </PrivateRoute>
          </Route>
          <Route path={routes.operations}>
            <PrivateRoute>
              <JobRuleSet />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.deliveryPipeline}>
            <PrivateRoute>
              <DeliveryPipelines />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.pipelineHistory}>
            <PrivateRoute>
              <PipelineHistory />
            </PrivateRoute>
          </Route>

          {/* Analytics & Assessment */}
          <Route exact path={routes.webAnalytics}>
            <PrivateRoute>
              <WebAnalytics />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.assessment}>
            <PrivateRoute>
              <Assessments />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.assess}>
            <PrivateRoute>
              <AssessDashboard />
              {/* Iframe screen for DCA  */}
            </PrivateRoute>
          </Route>

          {/* Migration & Transformation */}
          <Route exact path={routes.migrate}>
            <PrivateRoute>
              <MigrateDashboard />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.brownfield}>
            <PrivateRoute>
              <TransformDashboard />
            </PrivateRoute>
          </Route>

          {/* Project Management */}
          <Route exact path={routes.deliver}>
            <PrivateRoute>
              <DigitalPMODashboard />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.test}>
            <PrivateRoute>
              <Test />
            </PrivateRoute>
          </Route>

          {/* Compliance & Policies */}
          <Route exact path={routes.compliancePolicies}>
            <PrivateRoute>
              <CompliancePolicies />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.industryCompliancePolicies}>
            <PrivateRoute>
              <IndustryCompliancePolicies />
            </PrivateRoute>
          </Route>

          {/* Support & Documentation */}
          <Route exact path={routes.documentation}>
            <PrivateRoute>
              <Documentation />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.keyContacts}>
            <PrivateRoute
              isMobile={isMobile}
              showProjectSelector={false}
              pageTitle={NavMenuOptions.KEY_CONTACTS}
            >
              {isMobile ? <KeyContactsMobile /> : <KeyContacts />}
            </PrivateRoute>
          </Route>
          <Route exact path={routes.quickLinks}>
            <PrivateRoute>
              <QuickLinks />
            </PrivateRoute>
          </Route>

          {/* Feedback System */}
          <Route exact path={routes.feedback}>
            <PrivateRoute>
              <FeedbackForm />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.giveFeedback}>
            <PrivateRoute>
              <GiveFeedbackForm />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.feedbackResponse}>
            <PrivateRoute>
              <FeedbackResponse />
            </PrivateRoute>
          </Route>

          {/* ============================================ */}
          {/* REMOVE CANDIDATES - Unused or problematic   */}
          {/* ============================================ */}
          
          {/* DCA Iframe Routes - Duplicate functionality */}
          <Route exact path={routes.dcaManage}>
            <PrivateRoute isMobile={isMobile} hideHomePage>
              <Operate />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.dcaMigrate}>
            <PrivateRoute hideHomePage>
              <MigrateDashboard />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.dcaDeliver}>
            <PrivateRoute hideHomePage>
              <DigitalPMODashboard />
            </PrivateRoute>
          </Route>
          <Route exact path={routes.dcaTest}>
            <PrivateRoute hideHomePage>
              <Test />
            </PrivateRoute>
          </Route>

          {/* CBO Routes - Unused functionality */}
          <Route exact path={routes.cboLogin}>
            <CBOLogin />
          </Route>
          <Route exact path={routes.cboConfigureJourney}>
            <PrivateRoute>
              <ConfigureCBOJourney />
            </PrivateRoute>
          </Route>
          
          {/* SYSTEM ROUTES - Basic infrastructure        */}
          <Route exact path={routes.iframe}>
            <IFrameDrawer visible={true} /> 
          </Route>


          {/* ============================================ */}
          {/* DISABLED ROUTES - Commented out             */}
          {/* ============================================ */}

          {/* ============================================ */}
          {/* DEFAULT ROUTE - Fallback                    */}
          {/* ============================================ */}
          <Route path="/">
            <Redirect to={isAuthenticated() ? routes.dashboard : routes.login.viewGeneric} />
          </Route>
        </Switch>
        </Suspense>
      </Router>
    </>
  );
};

export default Layout;
